import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  IconButton,
  useDisclosure,
  Text,
  HStack,
  VStack,
  Center,
  Icon,
  Portal,
  useColorModeValue,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MdAdd, MdDelete, MdEdit, MdPlaylistAdd } from "react-icons/md";
import Card from "components/card/Card";
import {
  apiListDeviationCategories,
  apiCreateDeviationCategory,
  apiPatchDeviationCategory,
  apiDeleteDeviationCategory,
  type DeviationCategory,
} from "api/simulation/deviationCategories";

type CategoryForm = {
  name: string;
  description: string;
  /** Entero 0–32767 (Django PositiveSmallIntegerField); "" = usar default del servidor al crear. */
  severity_level: number | "";
};

const emptyForm: CategoryForm = { name: "", description: "", severity_level: "" };


function parseSeverityForApi(value: number | ""): number | undefined {
  if (value === "") {
    return undefined;
  }
  return value;
}

const PAGE_SIZE = 10;

export default function DeviationCategoriesAdmin() {
  const toast = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState<CategoryForm>(emptyForm);
  const [editing, setEditing] = useState<DeviationCategory | null>(null);
  const [page, setPage] = useState(1);
  const processedCreateQueryRef = useRef(false);
  const panelBg = useColorModeValue("white", "navy.800");
  const openFromCreateQuery = searchParams.get("action") === "create";

  const { data, isLoading, isError, error, isPreviousData } = useQuery(
    ["deviation-categories", page, PAGE_SIZE],
    () => apiListDeviationCategories({ page, page_size: PAGE_SIZE }),
    { retry: 1, keepPreviousData: true }
  );

  const categories = data?.results ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  useEffect(() => {
    if (!data) {
      return;
    }
    const last = Math.max(1, Math.ceil(data.count / PAGE_SIZE));
    if (page > last) {
      setPage(last);
    }
  }, [data, page]);

  const resetModal = useCallback(() => {
    setForm(emptyForm);
    setEditing(null);
  }, []);

  const openCreate = useCallback(() => {
    resetModal();
    onOpen();
  }, [onOpen, resetModal]);

  const openEdit = useCallback(
    (row: DeviationCategory) => {
      setEditing(row);
      setForm({
        name: row.name,
        description: row.description ?? "",
        severity_level:
          row.severity_level !== undefined && row.severity_level !== null
            ? row.severity_level
            : "",
      });
      onOpen();
    },
    [onOpen]
  );

  useEffect(() => {
    if (!openFromCreateQuery) {
      processedCreateQueryRef.current = false;
      return;
    }
    if (processedCreateQueryRef.current) {
      return;
    }
    processedCreateQueryRef.current = true;
    resetModal();
    onOpen();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("action");
        return next;
      },
      { replace: true }
    );
  }, [openFromCreateQuery, setSearchParams, onOpen, resetModal]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const createMut = useMutation(apiCreateDeviationCategory, {
    onSuccess: () => {
      qc.invalidateQueries(["deviation-categories"]);
      toast({ title: "Categoría creada", status: "success" });
      onClose();
      resetModal();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0] ??
        "No se pudo crear la categoría";
      toast({ title: String(msg), status: "error" });
    },
  });

  const patchMut = useMutation(
    ({ id, body }: { id: string; body: CategoryForm }) =>
      apiPatchDeviationCategory(id, {
        name: body.name,
        description: body.description,
        ...(parseSeverityForApi(body.severity_level) !== undefined
          ? { severity_level: parseSeverityForApi(body.severity_level)! }
          : {}),
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(["deviation-categories"]);
        toast({ title: "Categoría actualizada", status: "success" });
        onClose();
        resetModal();
      },
      onError: () => {
        toast({ title: "No se pudo actualizar", status: "error" });
      },
    }
  );

  const deleteMut = useMutation(apiDeleteDeviationCategory, {
    onSuccess: () => {
      qc.invalidateQueries(["deviation-categories"]);
      toast({ title: "Categoría eliminada", status: "success" });
    },
    onError: () => {
      toast({
        title: "No se pudo eliminar (puede estar en uso)",
        status: "error",
      });
    },
  });

  const submit = () => {
    if (!form.name.trim()) {
      toast({ title: "El nombre es obligatorio", status: "warning" });
      return;
    }
    if (form.severity_level !== "") {
      const s = form.severity_level;
      if (!Number.isInteger(s) || s < 0) {
        toast({
          title: `El nivel de severidad debe ser un entero mayor a 0`,
          status: "warning",
        });
        return;
      }
    }
    if (editing) {
      patchMut.mutate({ id: editing.id, body: form });
    } else {
      createMut.mutate({
        name: form.name.trim(),
        description: form.description.trim(),
        severity_level: parseSeverityForApi(form.severity_level),
      });
    }
  };

  const listEmpty = !isLoading && !isError && (data?.count ?? 0) === 0;

  return (
    <Box pt={{ base: "95px", md: "80px" }} w="100%">
      <Card py={{ base: 4, md: 5 }} px={{ base: "16px", md: "24px" }} mb="24px">
        <HStack mb="6" justify="space-between" flexWrap="wrap" gap={3} align="flex-start">
          <Text fontSize="sm" color="gray.600" maxW={{ md: "60%" }}>
            Gestión del catálogo de categorías de desviación.
          </Text>
          <Button
            colorScheme="brand"
            onClick={openCreate}
            leftIcon={<Icon as={MdPlaylistAdd} w="20px" h="20px" />}
            isDisabled={isLoading && !isPreviousData}
          >
            Nueva categoría
          </Button>
        </HStack>

        {isLoading && !isPreviousData ? (
          <Text>Cargando…</Text>
        ) : isError ? (
          <Box>
            <Text color="red.400" mb={4}>
              Error al cargar el listado. Puedes intentar crear una categoría si el
              servicio de alta está disponible.
            </Text>
            <Text fontSize="sm" color="gray.500" mb={4}>
              {(error as Error)?.message ?? "Error desconocido"}
            </Text>
            <Button colorScheme="brand" variant="outline" onClick={() => qc.invalidateQueries(["deviation-categories"])}>
              Reintentar carga
            </Button>
          </Box>
        ) : listEmpty ? (
          <Center
            borderWidth="1px"
            borderRadius="xl"
            borderStyle="dashed"
            py={16}
            px={6}
            bg="whiteAlpha.50"
          >
            <VStack spacing={5} textAlign="center" maxW="md">
              <Text fontWeight="600" fontSize="md">
                Aún no hay categorías de desviación
              </Text>
              <Text fontSize="sm" color="gray.500">
                Creá la primera categoría para clasificar desviaciones en las simulaciones.
                Necesitás un nombre; la descripción y el código son opcionales.
              </Text>
              <Button
                colorScheme="brand"
                size="lg"
                onClick={openCreate}
                leftIcon={<Icon as={MdPlaylistAdd} w="22px" h="22px" />}
              >
                Crear primera categoría
              </Button>
            </VStack>
          </Center>
        ) : (
          <>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Nombre</Th>
                    <Th>Descripción</Th>
                    <Th>Nivel de severidad</Th>
                    <Th w="100px">Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {categories.map((c) => (
                    <Tr key={c.id}>
                      <Td>{c.name}</Td>
                      <Td>{c.description || "—"}</Td>
                      <Td>
                        {c.severity_level !== undefined && c.severity_level !== null
                          ? String(c.severity_level)
                          : "—"}
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Editar"
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(c)}
                          />
                          <IconButton
                            aria-label="Eliminar"
                            icon={<MdDelete />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => {
                              if (window.confirm(`¿Eliminar «${c.name}»?`)) {
                                deleteMut.mutate(c.id);
                              }
                            }}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </>
        )}

        {!listEmpty && !isError && data ? (
          <HStack justify="space-between" mt={4} flexWrap="wrap" gap={3}>
            <Text fontSize="sm" color="gray.600">
              {data.count} categoría{data.count !== 1 ? "s" : ""} · Página {page} de {totalPages}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                isDisabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </HStack>
          </HStack>
        ) : null}
      </Card>

      {isOpen ? (
        <Portal>
          <Box
            position="fixed"
            inset={0}
            zIndex={2000}
            bg="blackAlpha.600"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
            aria-hidden
          />
          <Box
            role="dialog"
            aria-modal="true"
            aria-labelledby="deviation-category-dialog-title"
            position="fixed"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
            zIndex={2001}
            w={{ base: "calc(100vw - 2rem)", md: "460px" }}
            maxH="90vh"
            overflowY="auto"
            bg={panelBg}
            borderRadius="md"
            boxShadow="xl"
            px={6}
            py={5}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Text id="deviation-category-dialog-title" fontSize="xl" fontWeight="bold" mb={4}>
              {editing ? "Editar categoría" : "Nueva categoría"}
            </Text>
            <FormControl mb={4} isRequired>
              <FormLabel htmlFor="deviation-category-name">Nombre</FormLabel>
              <Input
                id="deviation-category-name"
                name="deviation-category-name"
                autoComplete="off"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel htmlFor="deviation-category-description">Descripción</FormLabel>
              <Textarea
                id="deviation-category-description"
                name="deviation-category-description"
                autoComplete="off"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel htmlFor="deviation-category-severity">Nivel de severidad</FormLabel>
              <Input
                id="deviation-category-severity"
                name="severity_level"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                autoComplete="off"
                value={form.severity_level === "" ? "" : String(form.severity_level)}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    setForm((f) => ({ ...f, severity_level: "" }));
                    return;
                  }
                  const n = Number.parseInt(raw, 10);
                  setForm((f) => ({
                    ...f,
                    severity_level: Number.isNaN(n) ? "" : n,
                  }));
                }}
              />
            </FormControl>

            <HStack justify="flex-end" gap={2} pt={2}>
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="brand"
                onClick={submit}
                isLoading={createMut.isLoading || patchMut.isLoading}
              >
                Guardar
              </Button>
            </HStack>
          </Box>
        </Portal>
      ) : null}
    </Box>
  );
}
